<template>
  <div class="min-h-screen pt-24" style="background: var(--order-bg); color: var(--order-text);">
    <div class="max-w-4xl mx-auto px-4 py-8">
      <!-- Header -->
      <div class="text-center mb-12 rounded-lg p-10 shadow-lg"
           style="background: var(--order-header-bg); border: 1px solid var(--border);">
        <div class="max-w-2xl mx-auto">
          <h1 class="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-bold mb-4 tracking-wide"
              style="color: var(--order-title); font-family: 'Cinzel', serif;">
            {{ $t("delivery.title") }}
          </h1>
          <div class="w-24 h-1 mx-auto mb-6 rounded-full" style="background: var(--primary);"></div>
          <p class="text-xs sm:text-base md:text-lg lg:text-xl leading-relaxed" style="color: var(--order-muted);">
            {{ $t("delivery.subtitle") }}
          </p>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-20">
        <div class="animate-spin rounded-full h-12 w-12 mx-auto border-b-2" style="border-color: var(--primary);"></div>
        <p class="mt-6 text-xs sm:text-base md:text-lg lg:text-xl" style="color: var(--order-muted);">
          {{ $t("delivery.loading") }}
        </p>
      </div>

      <!-- No Orders State -->
      <div v-else-if="!loading && orders.length === 0" class="text-center py-20">
        <img src="@/assets/Order/no-order.png" alt="No Orders" class="w-32 h-32 mx-auto mb-8 opacity-50" />
        <h2 class="text-lg sm:text-2xl md:text-4xl font-semibold mb-6" style="color: var(--order-text);">
          {{ $t("delivery.noOrders") }}
        </h2>
        <p class="text-xs sm:text-base md:text-lg lg:text-xl mb-10 leading-relaxed" style="color: var(--order-muted);">
          {{ $t("delivery.noOrdersDesc") }}
        </p>
        <router-link
          to="/menu"
          class="inline-block font-bold px-10 py-4 rounded-lg transition text-xs sm:text-base md:text-lg"
          style="background: var(--primary); color: var(--on-primary);">
          {{ $t("delivery.orderNow") }}
        </router-link>
      </div>

      <!-- Orders List -->
      <div v-else class="space-y-8">
        <!-- Active Orders -->
        <div v-if="activeOrders.length > 0">
          <h2 class="text-lg sm:text-2xl md:text-3xl lg:text-3xl font-bold mb-6" :style="`color: var(--order-heading-color)`">
            {{ $t("delivery.activeOrders") }}
          </h2>
          <div class="space-y-6">
            <div
              v-for="order in activeOrders"
              :key="order.id"
              class="rounded-lg p-8"
              :style="`background: var(--order-card-bg); border-${$i18n.locale === 'ar' ? 'right' : 'left'}: 4px solid var(--order-card-border)`"
            >
              <div class="flex justify-between items-start mb-6">
                <div>
                  <h3 class="text-base sm:text-lg md:text-xl font-semibold" style="color: var(--order-text);">
                    {{ $t("delivery.orderNumber") }} #{{ order.id.slice(-8) }}
                  </h3>
                  <p class="text-xs sm:text-sm md:text-base" style="color: var(--order-muted);">
                    {{ formatDate(order.createdAt) }}
                  </p>
                </div>
                <span class="px-3 py-1 text-xs sm:text-sm font-semibold rounded-full"
                      style="background: var(--primary); color: var(--on-primary);">
                  {{ getStatusText(order.status) }}
                </span>
              </div>

              <!-- Delivery Info -->
              <div class="mb-6">
                <p class="text-xs sm:text-base md:text-lg mb-3" style="color: var(--order-text);">
                  <span class="font-semibold">{{ $t("delivery.deliveryAddress") }}:</span>
                  {{ order.deliveryAddress }}
                </p>
                <p class="text-xs sm:text-base md:text-lg mb-3" style="color: var(--order-text);">
                  <span class="font-semibold">{{ $t("delivery.paymentMethod") }}:</span>
                  {{ getPaymentMethodText(order.paymentMethod) }}
                </p>
                <p class="text-xs sm:text-base md:text-lg font-semibold" style="color: var(--primary);">
                  🚚 {{ $t("delivery.deliveryTime") }}
                </p>
              </div>

              <!-- Order Items -->
              <div class="mb-6">
                <h4 class="font-semibold text-base sm:text-lg md:text-xl mb-3" style="color: var(--order-text);">
                  {{ $t("delivery.orderItems") }}:
                </h4>
                <div class="space-y-2">
                  <div v-for="dish in order.dishes" :key="dish.dishName"
                       class="flex justify-between text-xs sm:text-base md:text-lg"
                       style="color: var(--order-muted);">
                    <span>
                      {{ getTranslatedDishName(dish.dishName, t) }} x{{ dish.count }}
                    </span>
                    <span v-if="dish.specialRequest && dish.specialRequest !== 'none'"
                          class="text-xs sm:text-sm" style="color: var(--order-muted);">
                      ({{ dish.specialRequest }})
                    </span>
                  </div>
                </div>
              </div>

              <!-- Special Request -->
              <div v-if="order.specialRequest && order.specialRequest !== 'none'" class="mb-4">
                <h4 class="font-semibold text-base sm:text-lg md:text-xl mb-2" style="color: var(--order-text);">
                  {{ $t("delivery.specialRequest") }}:
                </h4>
                <p class="text-xs sm:text-base md:text-lg" style="color: var(--order-muted);">
                  {{ order.specialRequest }}
                </p>
              </div>

              <!-- Total -->
              <div class="pt-4" style="border-top: 1px solid var(--border);">
                <div class="flex justify-between text-xs sm:text-sm md:text-base mb-1" style="color: var(--order-muted);">
                  <span>{{ $t("delivery.subtotal") }}:</span>
                  <span>{{ $t("delivery.currency", { amount: order.subtotal.toFixed(2) }) }}</span>
                </div>
                <div class="flex justify-between text-xs sm:text-sm md:text-base mb-1" style="color: var(--order-muted);">
                  <span>{{ $t("delivery.serviceFee") }}:</span>
                  <span>{{ $t("delivery.currency", { amount: order.serviceFee.toFixed(2) }) }}</span>
                </div>
                <div class="flex justify-between text-base sm:text-lg md:text-xl font-semibold"
                     :style="`color: var(--order-total-color)`">
                  <span>{{ $t("delivery.total") }}:</span>
                  <span>{{ $t("delivery.currency", { amount: (order.subtotal + order.serviceFee).toFixed(2) }) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Previous Orders -->
        <div v-if="previousOrders.length > 0">
          <h2 class="text-lg sm:text-2xl md:text-4xl font-bold mb-6 mt-12" :style="`color: var(--order-heading-color)`">
            {{ $t("delivery.previousOrders") }}
          </h2>
          <div class="space-y-6">
            <div v-for="order in previousOrders" :key="order.id"
                 class="rounded-lg p-8"
                 :style="`background: var(--order-card-bg); border-${$i18n.locale === 'ar' ? 'right' : 'left'}: 4px solid var(--border)`">
              <div class="flex justify-between items-start mb-6">
                <div>
                  <h3 class="text-base sm:text-lg md:text-xl font-semibold" style="color: var(--order-text);">
                    {{ $t("delivery.orderNumber") }} #{{ order.id.slice(-8) }}
                  </h3>
                  <p class="text-xs sm:text-sm md:text-base" style="color: var(--order-muted);">
                    {{ formatDate(order.createdAt) }}
                  </p>
                </div>
                <span class="px-3 py-1 text-xs sm:text-sm font-semibold rounded-full"
                      style="background: var(--accent); color: var(--on-primary);">
                  {{ $t("delivery.delivered") }}
                </span>
              </div>

              <!-- Delivery Info -->
              <div class="mb-6">
                <p class="text-xs sm:text-base md:text-lg mb-3" style="color: var(--order-text);">
                  <span class="font-semibold">{{ $t("delivery.deliveryAddress") }}:</span>
                  {{ order.deliveryAddress }}
                </p>
                <p class="text-xs sm:text-base md:text-lg mb-3" style="color: var(--order-text);">
                  <span class="font-semibold">{{ $t("delivery.paymentMethod") }}:</span>
                  {{ getPaymentMethodText(order.paymentMethod) }}
                </p>
              </div>

              <!-- Order Items -->
              <div class="mb-6">
                <h4 class="font-semibold text-base sm:text-lg md:text-xl mb-3" style="color: var(--order-text);">
                  {{ $t("delivery.orderItems") }}:
                </h4>
                <div class="space-y-2">
                  <div v-for="dish in order.dishes" :key="dish.dishName"
                       class="flex justify-between text-xs sm:text-base md:text-lg"
                       style="color: var(--order-muted);">
                    <span>
                      {{ getTranslatedDishName(dish.dishName, t) }} x{{ dish.count }}
                    </span>
                    <span v-if="dish.specialRequest && dish.specialRequest !== 'none'"
                          class="text-xs sm:text-sm" style="color: var(--order-muted);">
                      ({{ dish.specialRequest }})
                    </span>
                  </div>
                </div>
              </div>

              <!-- Special Request -->
              <div v-if="order.specialRequest && order.specialRequest !== 'none'" class="mb-4">
                <h4 class="font-semibold text-base sm:text-lg md:text-xl mb-2" style="color: var(--order-text);">
                  {{ $t("delivery.specialRequest") }}:
                </h4>
                <p class="text-xs sm:text-base md:text-lg" style="color: var(--order-muted);">
                  {{ order.specialRequest }}
                </p>
              </div>

              <!-- Total -->
              <div class="pt-4" style="border-top: 1px solid var(--border);">
                <div class="flex justify-between text-xs sm:text-sm md:text-base mb-1" style="color: var(--order-muted);">
                  <span>{{ $t("delivery.subtotal") }}:</span>
                  <span>{{ $t("delivery.currency", { amount: order.subtotal.toFixed(2) }) }}</span>
                </div>
                <div class="flex justify-between text-xs sm:text-sm md:text-base mb-1" style="color: var(--order-muted);">
                  <span>{{ $t("delivery.serviceFee") }}:</span>
                  <span>{{ $t("delivery.currency", { amount: order.serviceFee.toFixed(2) }) }}</span>
                </div>
                <div class="flex justify-between text-base sm:text-lg md:text-xl font-semibold"
                     :style="`color: var(--order-total-color)`">
                  <span>{{ $t("delivery.total") }}:</span>
                  <span>{{ $t("delivery.currency", { amount: (order.subtotal + order.serviceFee).toFixed(2) }) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import { getTranslatedDishName } from "@/utils/dishTranslation";

const { t } = useI18n();
const auth = getAuth();
const db = getFirestore();
const loading = ref(true);
const orders = ref([]);
let authUnsubscribe = null;

const activeOrders = computed(() => orders.value.filter((o) => o.status !== "delivered"));
const previousOrders = computed(() => orders.value.filter((o) => o.status === "delivered"));

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString();
};

const getStatusText = (status) => {
  switch (status) {
    case "pending": return t("admin.orders.statuses.pending");
    case "confirmed": return t("admin.orders.statuses.confirmed");
    case "preparing": return t("admin.orders.statuses.preparing");
    case "out_for_delivery": return t("delivery.status.outForDelivery");
    case "cancelled": return t("admin.orders.statuses.cancelled");
    case "delivered": return t("admin.orders.statuses.delivered");
    default: return status;
  }
};

const getPaymentMethodText = (paymentMethod) => {
  switch (paymentMethod?.toLowerCase()) {
    case "cash": return t("delivery.paymentMethods.cash");
    case "card": return t("delivery.paymentMethods.card");
    default: return paymentMethod || t("delivery.paymentMethods.unknown");
  }
};

const fetchOrders = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return;

    const ordersRef = collection(db, "deliveryOrders");
    const q = query(ordersRef, where("userId", "==", user.uid));
    const querySnapshot = await getDocs(q);

    const ordersData = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.status !== "cancelled") {
        ordersData.push({ id: doc.id, ...data });
      }
    });

    ordersData.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA;
    });

    orders.value = ordersData;
  } catch (err) {
    console.error("Error fetching orders:", err);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  authUnsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) fetchOrders();
    else {
      orders.value = [];
      loading.value = false;
    }
  });
});

onUnmounted(() => {
  if (authUnsubscribe) authUnsubscribe();
});
</script>
